import { SettingsGroup, SettingsGroupItem } from '@sb/design-system';
import type { RestoreModelReport } from '@sb/feathers-types';

import { RestoreAlert } from './RestoreAlert';

interface RestoreModelReportPanelProps {
  report: RestoreModelReport;
}

function RestoreModelCount({
  label,
  value,
  'data-testid': dataTestId,
}: {
  label: string;
  value: number;
  'data-testid': string;
}) {
  return (
    <SettingsGroupItem>
      <span className="tw-flex-1">{label}</span>
      <span className="tw-text-label-secondary" data-testid={dataTestId}>
        {value}
      </span>
    </SettingsGroupItem>
  );
}

/**
 * What actually became of the models the operator asked for.
 *
 * Without this the shortfall is invisible: a restore reports "succeeded"
 * whether every model came over or none did, and a failed model download is
 * terminal on the cloud queue, so nothing retries it either.
 */
export function RestoreModelReportPanel({
  report,
}: RestoreModelReportPanelProps) {
  return (
    <section
      className="tw-flex tw-flex-col tw-gap-8"
      data-testid="restore-confirmation-model-report-region"
    >
      <h5 className="tw-heading-40 tw-pl-16">AI models</h5>

      <SettingsGroup>
        <RestoreModelCount
          label="Selected"
          value={report.requested}
          data-testid="restore-confirmation-models-requested"
        />
        <RestoreModelCount
          label="Queued to download"
          value={report.enqueued}
          data-testid="restore-confirmation-models-enqueued"
        />
        <RestoreModelCount
          label="Already on this robot"
          value={report.alreadyPresent}
          data-testid="restore-confirmation-models-present"
        />
      </SettingsGroup>

      {report.error && (
        <RestoreAlert data-testid="restore-confirmation-model-request-failed">
          The request failed: {report.error}
        </RestoreAlert>
      )}

      {report.unknownTrainingRunIds.length > 0 && (
        <RestoreAlert data-testid="restore-confirmation-model-missing">
          {report.unknownTrainingRunIds.length} model(s) no longer exist in the
          cloud and cannot be restored.
        </RestoreAlert>
      )}

      {report.failedTrainingRunIds.length > 0 && (
        <RestoreAlert data-testid="restore-confirmation-model-not-queued">
          {report.failedTrainingRunIds.length} model(s) could not be queued.
        </RestoreAlert>
      )}

      {report.failedDownloadTrainingRunIds.length > 0 && (
        <RestoreAlert data-testid="restore-confirmation-model-download-failed">
          {report.failedDownloadTrainingRunIds.length} model(s) failed to
          download and will not be retried automatically.
        </RestoreAlert>
      )}

      {!report.downloadsEnabledOnBox && report.enqueued > 0 && (
        <p className="tw-text-13 tw-text-label-tertiary tw-px-16">
          Queued models will not download until this robot runs in GPU mode or
          has local inference enabled.
        </p>
      )}
    </section>
  );
}
